import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Clock, Gift, Percent, ShoppingBag, Star, Plus, Check, ArrowRight, Instagram, Utensils, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from '../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import NoiseBg from '../assets/noise.svg';

const Deals = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedDeal, setAddedDeal] = useState(null);
  const [viewingImages, setViewingImages] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get('/deals');
      setDeals(response.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching deals:', error);
      setLoading(false);
    }
  };



  const handleAddDealToCart = (deal) => {
    const dealItem = {
      id: deal.id,
      name: deal.title,
      description: deal.description,
      price: Number(deal.deal_price), // MAP FROM deal_price
      image: deal.image,
      category: deal.category || 'deal',
      availability: 'ready-made',
      isDeal: true,
      originalPrice: Number(deal.original_price), // MAP FROM original_price
      items: deal.items_json // MAP FROM items_json
    };

    addToCart(dealItem, 1, [], {});
    setAddedDeal(deal.id);

    setTimeout(() => {
      setAddedDeal(null);
    }, 2000);
  };

  const howToUse = [
    { step: '1', title: 'Browse Deals', description: 'Check out our amazing deal offers' },
    { step: '2', title: 'Add to Cart', description: 'Click "Add to Cart" on any deal you like' },
    { step: '3', title: 'Checkout', description: 'Proceed to checkout with your deal' },
    { step: '4', title: 'Enjoy Savings', description: 'Get your delicious food at discounted prices!' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gold-400 text-xl font-medium">Loading Deals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-900/10 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gold-500/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        {/* Header */}
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16 relative"
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-red-500/20 rounded-full blur-3xl animate-pulse-slow" />
          <div className="inline-flex items-center justify-center p-4 bg-black/40 backdrop-blur-xl border border-red-500/30 rounded-full mb-8 relative group shadow-neon-red">
            <Flame className="w-8 h-8 text-red-500 relative z-10 animate-fire" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-500 via-gold-500 to-red-500 mb-6 font-serif animate-gradient-x drop-shadow-lg tracking-tight">
            Exclusive Deals
          </h1>
          <p className="text-xl text-gold-100/60 max-w-2xl mx-auto leading-relaxed font-light tracking-wide uppercase">
            Limited Time Offers • Premium Savings
          </p>
        </motion.div>

        {/* Active Deals Banner */}
        {deals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-red-900/40 to-gold-600/20 rounded-2xl p-6 mb-16 border border-red-500/30 backdrop-blur-sm relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50 animate-shimmer" />
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
              <Flame className="text-red-500 w-8 h-8 animate-pulse" />
              <p className="text-gold-100 text-lg font-bold tracking-wide">
                <span className="text-red-400">{deals.length} Active Deals</span> Available! Grab them before they expire!
              </p>
              <Flame className="text-red-500 w-8 h-8 animate-pulse" />
            </div>
          </motion.div>
        )}

        {/* Deals Grid */}
        <section className="mb-24">
          {deals.length === 0 ? (
            <div className="text-center py-20 bg-gray-900/30 rounded-3xl border border-dashed border-gray-700">
              <Gift className="w-20 h-20 text-gray-700 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-300 mb-3">No Active Deals</h2>
              <p className="text-gray-500">Check back soon for exciting offers!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {deals.map((deal, index) => (
                <motion.div
                  key={deal.id || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 hover:border-gold-500/50 rounded-3xl overflow-hidden flex flex-col sm:flex-row h-full transition-all duration-300 hover:shadow-neon-gold group-hover:-translate-y-1">

                    {/* Left Side: Image */}
                    <div
                      className="w-full sm:w-2/5 p-4 relative"
                      onClick={() => {
                        const allImages = [deal.image, ...(deal.additional_images || [])].filter(Boolean);
                        if (allImages.length > 0) {
                          setViewingImages(allImages);
                          setCurrentImageIndex(0);
                        }
                      }}
                    >
                      <div className="h-full w-full rounded-2xl overflow-hidden relative min-h-[200px] cursor-pointer group/image">
                        {deal.image ? (
                          <img
                            src={deal.image}
                            alt={deal.title}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <Utensils className="w-12 h-12 text-white/20" />
                          </div>
                        )}
                        <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-lg">
                          {Math.round(((deal.original_price - deal.deal_price) / deal.original_price) * 100)}% OFF
                        </div>

                        {/* Multi-image Indicator */}
                        {deal.additional_images && deal.additional_images.length > 0 && (
                          <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 border border-white/10 group-hover/image:bg-gold-500 group-hover/image:text-black transition-colors">
                            <span className="w-1.5 h-1.5 bg-white rounded-full group-hover/image:bg-black" />
                            +{deal.additional_images.length + 1}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Perforated Line Visual (CSS border dashed) */}
                    <div className="hidden sm:block w-[1px] bg-gradient-to-b from-transparent via-white/20 to-transparent border-r border-dashed border-gray-600/50 my-6 relative">
                      <div className="absolute -top-6 -left-3 w-6 h-6 bg-black rounded-full" />
                      <div className="absolute -bottom-6 -left-3 w-6 h-6 bg-black rounded-full" />
                    </div>

                    {/* Right Side: Content */}
                    <div className="flex-1 p-6 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-2xl font-bold text-white group-hover:text-gold-400 transition-colors font-serif">{deal.title}</h3>
                        <div className="flex flex-col items-end">
                          <span className="text-xs text-gray-500 line-through decoration-red-500">Rs. {deal.original_price}</span>
                          <span className="text-xl font-bold text-gold-500">Rs. {deal.deal_price}</span>
                        </div>
                      </div>

                      <p className="text-gray-400 text-sm mb-4 line-clamp-2 font-light">{deal.description}</p>

                      <div className="bg-white/5 rounded-lg p-3 mb-4 backdrop-blur-sm">
                        <div className="flex items-center gap-2 text-gold-200 text-xs font-medium">
                          <Gift className="w-4 h-4 text-gold-500" />
                          <span className="line-clamp-1">{deal.items_json || "Special Bundle"}</span>
                        </div>
                      </div>

                      <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-gray-500 text-xs">
                          <Clock className="w-3 h-3" /> {deal.validity}
                        </div>
                        <Button
                          onClick={() => handleAddDealToCart(deal)}
                          disabled={addedDeal === deal.id}
                          size="sm"
                          className={cn(
                            "rounded-full font-bold transition-all duration-300 shadow-md",
                            addedDeal === deal.id
                              ? "bg-green-600 text-white"
                              : "bg-gold-500 text-black hover:bg-gold-400 hover:scale-105"
                          )}
                        >
                          {addedDeal === deal.id ? (
                            <>
                              <Check className="mr-1 h-4 w-4" /> Added
                            </>
                          ) : (
                            <>
                              <Plus className="mr-1 h-4 w-4" /> Grab Deal
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* How to Use */}
        <section className="mb-24 relative">
          <div className="absolute inset-0 bg-gold-900/5 blur-3xl rounded-full" />
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gold-500 mb-4 font-serif">How to Get Deals</h2>
              <p className="text-gray-400">Simple steps to big savings</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {howToUse.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="glass-card rounded-3xl p-8 border border-white/10 hover:border-gold-500/30 transition-all duration-300 text-center group h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gold-500/10 rounded-bl-full -mr-4 -mt-4 transition-all group-hover:bg-gold-500/20" />

                    <div className="w-16 h-16 bg-black/60 backdrop-blur-md rounded-2xl flex items-center justify-center text-gold-500 text-2xl font-bold mx-auto mb-6 shadow-neon-gold border border-gold-500/20 group-hover:scale-110 transition-transform">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold-400 transition-colors">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                  {index < howToUse.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                      <ArrowRight className="w-6 h-6 text-gold-500/50" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Terms & Conditions */}
        <section className="mb-24">
          <div className="bg-gray-900/40 rounded-3xl p-8 md:p-12 border border-white/5 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-gold-500 mb-8 font-serif border-b border-white/5 pb-4">
              Terms & Conditions
            </h2>
            <div className="grid md:grid-cols-2 gap-y-4 gap-x-8">
              {[
                'Deals cannot be combined with other offers or menu items',
                'Deals are only valid during specified time periods',
                'Items in deals cannot be customized or substituted',
                'Magic World reserves the right to modify or cancel deals at any time',
                'All deals are subject to availability',
                'Deal prices already include all discounts'
              ].map((term, idx) => (
                <div key={idx} className="flex items-start gap-3 text-gray-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-500 mt-2 flex-shrink-0" />
                  <span className="text-sm leading-relaxed">{term}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA & Social */}
        <section className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 border border-gold-500/20 text-center flex flex-col justify-center items-center"
          >
            <h2 className="text-3xl font-bold text-white mb-4 font-serif">Want Regular Items?</h2>
            <p className="text-gray-400 mb-8 max-w-sm">Mix and match our special deals with items from our complete menu.</p>
            <Button
              onClick={() => navigate('/menu')}
              variant="outline"
              className="border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black rounded-full px-8 py-6 text-lg"
            >
              <ShoppingBag className="mr-2 h-5 w-5" /> Browse Full Menu
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-pink-900/20 to-purple-900/20 rounded-3xl p-10 border border-white/5 text-center flex flex-col justify-center items-center relative overflow-hidden group"
          >
            <div
              className="absolute inset-0 opacity-5"
              style={{ backgroundImage: `url(${NoiseBg})` }}
            />
            <h3 className="text-3xl font-bold text-white mb-4">Flash Sales! ⚡</h3>
            <p className="text-gray-300 mb-8 max-w-sm">Follow us on social media to never miss our limited-time flash offers.</p>
            <a
              href="https://www.instagram.com/mw_cook_with_magic?igsh=MTU2YjVnNXN4dzRhNA=="
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold shadow-lg shadow-pink-500/20 hover:scale-105 transition-all flex items-center gap-2"
            >
              <Instagram className="w-5 h-5" /> Follow on Instagram
            </a>
          </motion.div>
        </section>
      </div>

      {/* Image Gallery */}
      <AnimatePresence>
        {viewingImages && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-xl"
            onClick={() => setViewingImages(null)}
          >
            <div className="relative max-w-5xl w-full h-[80vh] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 text-white hover:bg-white/10 rounded-full z-50"
                onClick={() => setViewingImages(null)}
              >
                <X className="w-8 h-8" />
              </Button>

              <div className="relative w-full h-full flex items-center justify-center">
                <AnimatePresence mode='wait'>
                  <motion.img
                    key={currentImageIndex}
                    src={viewingImages[currentImageIndex]}
                    alt={`View ${currentImageIndex + 1}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
                  />
                </AnimatePresence>

                {viewingImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + viewingImages.length) % viewingImages.length); }}
                      className="absolute left-4 bg-black/50 hover:bg-gold-500 hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % viewingImages.length); }}
                      className="absolute right-4 bg-black/50 hover:bg-gold-500 hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  </>
                )}
              </div>

              {viewingImages.length > 1 && (
                <div className="absolute -bottom-20 flex gap-2 overflow-x-auto max-w-full py-2 px-4 scrollbar-hide">
                  {viewingImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      className={cn(
                        "relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0",
                        index === currentImageIndex ? "border-gold-500 scale-110" : "border-transparent opacity-50 hover:opacity-100"
                      )}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Deals;



